// Miro-style secondary panel for the sticky tool. Mounted next to the
// toolbar whenever the sticky / sticky-wide tool is active. Each color
// tile is both a click target (arm next-create with this color) and a
// drag source (drop a sticky of this color anywhere on the canvas).
//
// Drag detection mirrors the sticky toolbar buttons: pointerdown starts a
// movement watch, threshold crossing promotes to a drag and suppresses the
// trailing click so the same gesture never does both things.

import { useRef } from 'react';
import type { StickyColor } from '@frame/shared';
import { STICKY_COLORS } from '@frame/shared';
import { useTokens } from '../styles/theme';

const DRAG_THRESHOLD_PX = 4;

interface Props {
  variant: 'square' | 'wide';
  currentColor: StickyColor;
  onPickColor: (color: StickyColor) => void;
  onStartDrag?: (
    variant: 'square' | 'wide',
    color: StickyColor,
    screenPos: { x: number; y: number },
  ) => void;
}

export function StickyColorPopover({ variant, currentColor, onPickColor, onStartDrag }: Props) {
  const tokens = useTokens();
  return (
    <div
      role="group"
      aria-label="Sticky color"
      className="
        absolute left-full top-0 ml-2
        bg-bg-panel border border-border-default rounded-2 shadow-lg
        p-2 z-[6]
        grid grid-cols-[auto_auto] gap-1.5
      "
      onMouseDown={(e) => e.stopPropagation()}
    >
      {STICKY_COLORS.map((color) => (
        <ColorTile
          key={color}
          color={color}
          fill={tokens.sticky[color]}
          selected={color === currentColor}
          onPick={() => onPickColor(color)}
          onStartDrag={
            onStartDrag
              ? (pos) => onStartDrag(variant, color, pos)
              : undefined
          }
        />
      ))}
    </div>
  );
}

interface TileProps {
  color: StickyColor;
  fill: string;
  selected: boolean;
  onPick: () => void;
  onStartDrag?: (pos: { x: number; y: number }) => void;
}

function ColorTile({ color, fill, selected, onPick, onStartDrag }: TileProps) {
  const draggedRef = useRef(false);
  const handlePointerDown = onStartDrag
    ? (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        const startX = e.clientX;
        const startY = e.clientY;
        draggedRef.current = false;
        const onMove = (m: PointerEvent) => {
          const dx = m.clientX - startX;
          const dy = m.clientY - startY;
          if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
          draggedRef.current = true;
          cleanup();
          onStartDrag({ x: m.clientX, y: m.clientY });
        };
        const onUp = () => cleanup();
        const cleanup = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }
    : undefined;
  const handleClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    onPick();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      title={color}
      aria-label={`Sticky color ${color}${selected ? ' (selected)' : ''}`}
      aria-pressed={selected}
      className={[
        'w-7 h-7 rounded-1',
        'cursor-grab active:cursor-grabbing',
        'transition-transform duration-micro ease-out',
        'focus-visible:outline-none focus-visible:shadow-focus',
        selected ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg-panel' : 'hover:scale-105',
      ].join(' ')}
      style={{ background: fill }}
    />
  );
}
